<template>
    <div class="flex flex-col gap-7">
        <page-items-content
            :is-loading="navStore.isLoading"
            :page-items="pageItems"
        />
    </div>
</template>

<script setup lang="ts">
    import { nextTick, onMounted, onUnmounted, ref, type Ref } from 'vue';
    import { useAccountStore } from '~/stores/accountStore';
    import { useNavigationStore } from '~/stores/navigationStore';
    import { getCustomPageData } from '~/utils/dataProviders/CustomPageApiDataProvider';
    import { useRoute } from 'vue-router';
    import type { IPageItem } from '~/types/types';
    import PageItemsContent from '~/components/pageContent/PageItemsContent.vue';
    import { useModalStore } from '~/stores/modalStore';

    const props = defineProps({
        slug: {
            type: String,
            required: false,
            default: null,
        },
        hiddenHeader: {
            type: Boolean,
            required: false,
            default: false,
        },
    });

    const modalStore = useModalStore();
    const route = useRoute();
    const pageItems: Ref<IPageItem[]> = ref([]);
    const navStore = useNavigationStore();
    const slug = props.slug || route.params.slug;

    const setPageData = async () => {
        navStore.setLoading(true);
        try {
            const pageData = await getCustomPageData(useAccountStore().state.id, slug as string);
            if (!navStore.state.currentPageTitle) {
                navStore.setPageDetails(pageData.title, pageData.description, props.hiddenHeader);
            } else {
                navStore.setPageDescription(pageData.description);
            }
            pageItems.value = pageData.customPageItems;
        }
        catch (error) {
            showError({ statusCode: 404, name: 'Custom page not found' });
        }
        navStore.setLoading(false);

        // register listeners for page builder images
        await nextTick();
        getPageBuilderImagesWithoutLink().forEach(image => {
            image.addEventListener('click', (event) => openModalWithImage(event, image));
        });
        getPageBuilderMultipleImagesWithoutLink().map(imageLink =>
            imageLink.addEventListener('click', (event) => openModalWithMultipleImages(event, imageLink))
        );
        getPageBuilderCarouselPrevControls().map(control => {
            control.addEventListener('click', () => moveCarousel(control, 'prev'));
        });
        getPageBuilderCarouselNextControls().map(control => {
            control.addEventListener('click', () => moveCarousel(control, 'next'));
        });
    };

    const getPageBuilderImagesWithoutLink = () => {
        const allImages = document.querySelectorAll('.pb-image-self-link');
        return Array.from(allImages).filter(image => !image.closest('.pb-multiple-images'));
    };

    const getPageBuilderMultipleImagesWithoutLink =  () => {
        const allMultipleImages = document.querySelectorAll('.pb-multiple-images .pb-image-self-link');
        return Array.from(allMultipleImages);
    };
    const getPageBuilderCarouselPrevControls = () => {
        return Array.from(document.querySelectorAll('.pb-carousel [data-target-prev]'));
    };

    const getPageBuilderCarouselNextControls = () => {
        return Array.from(document.querySelectorAll('.pb-carousel [data-target-next]'));
    };

    const moveCarousel = (element: Element, direction: 'prev' | 'next') => {
        const viewPort = element.closest('.pb-carousel')?.querySelector('.pb-carousel-viewport');
        if (!viewPort) {
            return;
        }

        const itemWidth = viewPort.querySelector('.pb-carousel-slide')?.clientWidth || 1;
        const visibleItemsCount = Math.floor(viewPort.clientWidth / itemWidth);
        const scrollOffset = viewPort.clientWidth / visibleItemsCount;

        if (direction === 'prev') {
            viewPort.scrollLeft = viewPort.scrollLeft <= 0
                ? viewPort.scrollWidth - viewPort.clientWidth
                : viewPort.scrollLeft - scrollOffset;
        } else {
            viewPort.scrollLeft = viewPort.scrollLeft < viewPort.scrollWidth - viewPort.clientWidth
                ? viewPort.scrollLeft + scrollOffset
                : 0;
        }
    };

    const openModalWithImage = (event: Event, imageLink: Element) => {
        event.preventDefault();

        const newImagePath = imageLink.getAttribute('href');
        modalStore.setModalData(
            imageLink.firstElementChild?.getAttribute('alt') || '',
            newImagePath ? [newImagePath] : [],
            true,
        );
    };

    const openModalWithMultipleImages =  (event: Event, imageLink: Element) => {
        event.preventDefault();

        const imageLinks = imageLink.closest('.pb-multiple-images')?.querySelectorAll('.pb-image-self-link') ?? [];
        const imagePaths = Array.from(imageLinks).map(image => (image.getAttribute('href'))).filter(image => image !== null) as string[];
        const imageHref = imageLink.getAttribute('href');
        const imageIndex = imagePaths.findIndex(image => image === imageHref);
        const initialImageIndex = imageIndex < 0 ? 0 : imageIndex;
        modalStore.setModalData(
            imageLink.firstElementChild?.getAttribute('alt') || '',
            imagePaths,
            true,
            initialImageIndex,
        );
    };

    onMounted( async () => {
        await nextTick();
        await setPageData();
    });

    onUnmounted(() => {
        getPageBuilderImagesWithoutLink().forEach(image => {
            image.removeEventListener('click', (event) => openModalWithImage(event, image));
        });
        getPageBuilderMultipleImagesWithoutLink().map(imageLink =>
            imageLink.removeEventListener('click', (event) => openModalWithMultipleImages(event, imageLink))
        );
        getPageBuilderCarouselPrevControls().map(control => {
            control.removeEventListener('click', () => moveCarousel(control, 'prev'));
        });
        getPageBuilderCarouselNextControls().map(control => {
            control.removeEventListener('click', () => moveCarousel(control, 'next'));
        });
    });
</script>
